import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, MoreVertical, Edit, Trash2, Eye, Target, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = [
  { value: 'always_on_acquisition', label: 'Always-On Acquisition' },
  { value: 'monthly_office_event', label: 'Monthly Office Event' },
  { value: 'sales_week', label: 'Sales Week' },
];

const getTypeLabel = (type) => {
  const found = CAMPAIGN_TYPES.find(t => t.value === type);
  return found ? found.label : type;
};

const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'always_on_acquisition': return 'bg-blue-100 text-blue-800';
    case 'monthly_office_event': return 'bg-purple-100 text-purple-800';
    case 'sales_week': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'always_on_acquisition',
    start_date: '',
    end_date: '',
    description: ''
  });
  
  const { hasPermission, role } = usePermissions();
  const canCreate = hasPermission('campaigns_create');
  const canUpdate = hasPermission('campaigns_update');
  const canDelete = hasPermission('campaigns_delete');
  const isReadOnly = role === 'top_management' || role === 'finance';

  useEffect(() => {
    fetchCampaigns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined
      };
      if (formData.start_date) payload.start_date = new Date(formData.start_date).toISOString();
      if (formData.end_date) payload.end_date = new Date(formData.end_date).toISOString();
      
      const response = await fetch(`${API_URL}/api/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Campaign created successfully');
        setIsCreateOpen(false);
        resetForm();
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create campaign');
      }
    } catch (error) {
      logger.error('Create campaign error:', error);
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined
      };
      if (formData.start_date) payload.start_date = new Date(formData.start_date).toISOString();
      if (formData.end_date) payload.end_date = new Date(formData.end_date).toISOString();
      
      const response = await fetch(`${API_URL}/api/campaigns/${selectedCampaign.campaign_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Campaign updated successfully');
        setIsEditOpen(false);
        resetForm();
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update campaign');
      }
    } catch (error) {
      logger.error('Update campaign error:', error);
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign? All campaign members will also be removed.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Campaign deleted successfully');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete campaign');
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const openEditDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name,
      type: campaign.type,
      start_date: campaign.start_date ? format(new Date(campaign.start_date), "yyyy-MM-dd'T'HH:mm") : '',
      end_date: campaign.end_date ? format(new Date(campaign.end_date), "yyyy-MM-dd'T'HH:mm") : '',
      description: campaign.description || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'always_on_acquisition',
      start_date: '',
      end_date: '',
      description: ''
    });
    setSelectedCampaign(null);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || campaign.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  const CampaignForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Campaign Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="input-goldsands"
          data-testid="campaign-name"
        />
      </div>

      <div>
        <Label htmlFor="type">Campaign Type *</Label>
        <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
          <SelectTrigger data-testid="campaign-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CAMPAIGN_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_date">Start Date</Label>
          <Input
            id="start_date"
            type="datetime-local"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className="input-goldsands"
            data-testid="campaign-start"
          />
        </div>
        <div>
          <Label htmlFor="end_date">End Date</Label>
          <Input
            id="end_date"
            type="datetime-local"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="input-goldsands"
            data-testid="campaign-end"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-goldsands min-h-[80px]"
          data-testid="campaign-description"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); resetForm(); }}>
          Cancel
        </Button>
        <Button type="submit" className="btn-accent" data-testid="campaign-submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Read-only banner */}
      {isReadOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Eye className="text-blue-600" size={20} />
          <span className="text-blue-800">You have read-only access to campaigns.</span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Campaigns</h1>
          <p className="text-slate-500 font-body mt-1">Manage marketing and sales campaigns</p>
        </div>

        {canCreate && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-accent" data-testid="create-campaign-btn">
                <Plus size={20} className="mr-2" />
                Create Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New Campaign</DialogTitle>
              </DialogHeader>
              <CampaignForm onSubmit={handleCreateCampaign} submitLabel="Create Campaign" />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="card-goldsands">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-goldsands"
                data-testid="search-campaigns"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-type">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CAMPAIGN_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCampaigns.map(campaign => (
          <Card key={campaign.campaign_id} className="card-goldsands hover:shadow-lg transition-shadow cursor-pointer" data-testid={`campaign-card-${campaign.campaign_id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="font-heading text-lg text-slate-900">{campaign.name}</CardTitle>
                  <Badge className={`mt-2 ${getTypeBadgeColor(campaign.type)}`}>
                    {getTypeLabel(campaign.type)}
                  </Badge>
                </div>
                {!isReadOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`campaign-actions-${campaign.campaign_id}`}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}>
                        <Users size={16} className="mr-2" /> View Members
                      </DropdownMenuItem>
                      {canUpdate && (
                        <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
                          <Edit size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem 
                          onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CardHeader>
            <CardContent onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}>
              {campaign.description && (
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{campaign.description}</p>
              )}
              <div className="flex items-center gap-4 text-xs text-slate-500">
                {campaign.start_date && (
                  <span>Start: {format(new Date(campaign.start_date), 'MMM d, yyyy')}</span>
                )}
                {campaign.end_date && (
                  <span>End: {format(new Date(campaign.end_date), 'MMM d, yyyy')}</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCampaigns.length === 0 && (
        <Card className="card-goldsands">
          <CardContent className="py-12 text-center">
            <Target className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500">No campaigns found</p>
            {canCreate && (
              <Button className="btn-accent mt-4" onClick={() => setIsCreateOpen(true)}>
                Create your first campaign
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Campaign</DialogTitle>
          </DialogHeader>
          <CampaignForm onSubmit={handleUpdateCampaign} submitLabel="Update Campaign" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
